import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ABHHookHeatmap from './ABHHookHeatmap';
import OfferSearchSelect from './OfferSearchSelect';
import ABHCustomSession from './ABHChatPanel';
import { lifecycleClass, lifecycleLabel, lifecycleTitle } from "@/lib/lifecycleStage";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Framing Phase ──
const FramingPhase = ({ session, onComplete }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [direction, setDirection] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Resume: restore the full framing conversation, not just the first question.
    if (session.framing_history?.length > 0) {
      setMessages(session.framing_history);
    } else if (session.framing?.question) {
      setMessages([{ role: 'assistant', content: session.framing.question }]);
    }
    if (session.framing_complete || session.framing?.type === 'complete') {
      setComplete(true);
      setDirection(session.direction_summary || session.framing?.direction_summary || '');
    }
  }, [session]);

  const sendAnswer = async () => {
    if (!input.trim() || loading) return;
    const answer = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: answer }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/abh/sessions/${session.session_id}/frame`, { answer });
      if (res.data.type === 'complete') {
        setComplete(true);
        setDirection(res.data.direction_summary || '');
        setMessages(prev => [...prev, { role: 'assistant', content: `Direction established: ${res.data.direction_summary}` }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: res.data.question }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + (err.response?.data?.detail || err.message) }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="abh-framing-phase">
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
          <span className="text-sm font-medium text-teal-300">AI Framing</span>
          <span className="text-xs text-slate-500">Campaign: {session.campaign_purpose}</span>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${
                msg.role === 'user'
                  ? 'bg-teal-500/10 border border-teal-500/30 text-teal-100'
                  : 'bg-slate-700/50 border border-slate-600 text-slate-200'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-700/50 rounded-lg px-4 py-2.5 text-sm text-slate-400">
                <div className="flex gap-1"><span className="animate-bounce">.</span><span className="animate-bounce" style={{animationDelay:'0.1s'}}>.</span><span className="animate-bounce" style={{animationDelay:'0.2s'}}>.</span></div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {!complete ? (
          <div className="flex gap-2 mt-3">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendAnswer()}
              placeholder="Answer the framing question..."
              className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none"
              data-testid="framing-input"
              disabled={loading}
            />
            <button
              onClick={sendAnswer}
              disabled={loading || !input.trim()}
              data-testid="framing-send-btn"
              className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 disabled:opacity-50"
            >
              Send
            </button>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3">
              <p className="text-teal-300 text-sm font-medium">Framing Complete</p>
              <p className="text-slate-300 text-xs mt-1">{direction}</p>
            </div>
            <button
              onClick={() => onComplete(direction)}
              data-testid="proceed-to-psych-map-btn"
              className="w-full px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600"
            >
              Generate Psychology Map
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Psychology Map Phase ──
const PsychologyMapPhase = ({ sessionId, onComplete, initialEntries }) => {
  const [entries, setEntries] = useState(initialEntries || []);
  const [loading, setLoading] = useState(false);
  // If a map already exists (Resume), show it instead of regenerating.
  const [generated, setGenerated] = useState((initialEntries || []).length > 0);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/abh/sessions/${sessionId}/generate-psychology-map`);
      setEntries(res.data.entries || []);
      setGenerated(true);
    } catch (err) {
      alert('Failed to generate: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ABH sessions require no approval — the psychology map is persisted by the
  // generate endpoint (offer.abh_psychology_map) and angle generation reads it
  // directly. Operators advance straight to angles.

  // Mount-only: generate once when the phase component first renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!generated) generate(); }, []);

  return (
    <div className="space-y-4" data-testid="abh-psych-map-phase">
      <h3 className="text-lg font-medium text-white">Psychology Map</h3>

      {loading && !generated && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
          <p className="text-slate-400 text-sm mt-3">Generating ABH psychology map...</p>
        </div>
      )}

      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((e, i) => (
            <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
              <p className="text-white text-sm">{e.statement}</p>
              <div className="flex gap-2 mt-1.5 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded bg-teal-500/10 text-teal-400">{e.entry_point_type}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-400">{e.awareness_stage}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-400">{e.mechanism_link}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {generated && (
        <div className="flex gap-2">
          <button onClick={generate} disabled={loading} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 disabled:opacity-50" data-testid="regenerate-psych-btn">
            Regenerate
          </button>
          <button onClick={onComplete} disabled={loading} className="px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 disabled:opacity-50" data-testid="continue-to-angles-btn">
            Continue to Angles
          </button>
        </div>
      )}
    </div>
  );
};

// ── Angle Phase ──
const AnglePhase = ({ sessionId, onComplete, onSelectAngle, initialAngles, initialSelectedAngleId }) => {
  const [angles, setAngles] = useState(initialAngles || []);
  const [loading, setLoading] = useState(false);
  // If angles already exist (Resume), show them instead of regenerating.
  const [generated, setGenerated] = useState((initialAngles || []).length > 0);
  const [selectedAngle, setSelectedAngle] = useState(initialSelectedAngleId || null);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/abh/sessions/${sessionId}/generate-angles`);
      setAngles(res.data.angles || []);
      setGenerated(true);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ABH sessions require no approval — selecting an angle advances straight to
  // script generation.
  const selectAngle = (angleId) => {
    setSelectedAngle(angleId);
    onSelectAngle(angleId);
    onComplete();
  };

  // Mount-only: generate once when the phase component first renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!generated) generate(); }, []);

  return (
    <div className="space-y-4" data-testid="abh-angle-phase">
      <h3 className="text-lg font-medium text-white">Angle Generation</h3>

      {loading && !generated && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
          <p className="text-slate-400 text-sm mt-3">Generating ABH angles...</p>
        </div>
      )}

      {angles.map((angle) => (
        <div key={angle.angle_id} className={`bg-slate-800/50 border rounded-lg p-4 cursor-pointer transition-colors ${
          selectedAngle === angle.angle_id ? 'border-teal-500' : 'border-slate-700 hover:border-slate-600'
        }`}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <h4 className="text-white text-sm font-medium">{angle.angle_name}</h4>
              <p className="text-slate-400 text-xs mt-1">{angle.core_belief_shift}</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded bg-teal-500/10 text-teal-400">{angle.mechanism_focus}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded bg-slate-700 ${lifecycleClass(angle.lifecycle_stage, "text-slate-400")}`}
                  title={lifecycleTitle(angle.lifecycle_stage)}
                >
                  {lifecycleLabel(angle.lifecycle_stage)}
                </span>
                <span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-400">{angle.psychological_entry_point}</span>
              </div>
            </div>
            <button
              onClick={() => selectAngle(angle.angle_id)}
              disabled={loading}
              className="ml-3 px-3 py-1.5 bg-teal-500/10 text-teal-400 rounded text-xs hover:bg-teal-500/20 border border-teal-500/30 disabled:opacity-50"
              data-testid={`select-angle-${angle.angle_id}`}
            >
              Select
            </button>
          </div>
        </div>
      ))}

      {generated && (
        <button onClick={generate} disabled={loading} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 disabled:opacity-50" data-testid="regenerate-angles-btn">
          Regenerate Angles
        </button>
      )}
    </div>
  );
};

// ── Script Phase ──
const ScriptPhase = ({ sessionId, offerId, angleId, onComplete, initialData }) => {
  const [data, setData] = useState(initialData || null);
  const [loading, setLoading] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [routing, setRouting] = useState(false);
  const [routed, setRouted] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/abh/sessions/${sessionId}/generate-scripts?angle_id=${angleId}`);
      setData(res.data);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const routeToProduction = async (scriptId) => {
    setRouting(true);
    try {
      const res = await axios.post(`${API}/abh/sessions/${sessionId}/route-to-production?script_id=${scriptId}`);
      setRouted(res.data);
      onComplete(res.data);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRouting(false);
    }
  };

  // Mount-only: generate scripts/hooks once — unless Resume already provided them.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!data) generate(); }, []);

  return (
    <div className="space-y-4" data-testid="abh-script-phase">
      <h3 className="text-lg font-medium text-white">Scripts & Hooks</h3>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
          <p className="text-slate-400 text-sm mt-3">Generating scripts, hooks & CTAs...</p>
        </div>
      )}

      {data?.hooks && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-400">Hooks ({data.hooks_count})</h4>
          {data.hooks.map((h, i) => (
            <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
              <span className="text-xs text-teal-400 font-mono" title={h.element_number ? undefined : 'Not yet promoted to the library'}>{h.element_number || '—'}</span>
              <p className="text-white text-sm mt-1">{h.content}</p>
              <span className="text-xs text-slate-500">{h.hook_type}</span>
            </div>
          ))}
        </div>
      )}

      {data?.scripts && (
        <div className="space-y-3 mt-4">
          <h4 className="text-sm font-medium text-slate-400">Scripts ({data.scripts_count})</h4>
          {data.scripts.map((s, i) => (
            <div key={s.script_id} className={`bg-slate-800/50 border rounded-xl p-4 ${selectedScript === s.script_id ? 'border-teal-500' : 'border-slate-700'}`}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-xs text-teal-400 font-mono">Script {i + 1} - {s.total_duration_seconds}s</span>
                <span className="text-xs text-slate-500">Hook: {s.hook_number}</span>
              </div>
              {s.sections?.map((sec, j) => (
                <div key={j} className="mb-2 pl-3 border-l-2 border-slate-700">
                  <p className="text-xs text-slate-500">[{sec.section} - {sec.timing}]</p>
                  <p className="text-sm text-slate-200">{sec.spoken_text}</p>
                  {sec.visual_direction && <p className="text-xs text-slate-500 italic mt-0.5">Visual: {sec.visual_direction}</p>}
                </div>
              ))}
              {s.cta_variants?.map((cta, j) => (
                <div key={j} className="mt-2 bg-teal-500/5 border border-teal-500/20 rounded p-2">
                  <span className="text-xs text-teal-400">{cta.element_number || `CTA ${j + 1}`}</span>
                  <p className="text-sm text-white">{cta.content}</p>
                </div>
              ))}
              {!routed && (
                <button
                  onClick={() => { setSelectedScript(s.script_id); routeToProduction(s.script_id); }}
                  disabled={routing}
                  className="mt-3 px-4 py-2 bg-teal-500 text-white rounded-lg text-sm hover:bg-teal-600 disabled:opacity-50"
                  data-testid={`route-script-${s.script_id}`}
                >
                  {routing && selectedScript === s.script_id ? 'Routing...' : 'Generate Brief & Route to VE'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {routed && (
        <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4 mt-4" data-testid="abh-brief-complete">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            <span className="text-teal-300 font-medium text-sm">Brief Generated & Routed</span>
          </div>
          <p className="text-slate-300 text-xs font-mono">{routed.creative_identifier}</p>
          <p className="text-slate-500 text-xs mt-1">Uploaded to Google Drive. Telegram notification sent.</p>
        </div>
      )}

      {!loading && data && !routed && (
        <button onClick={generate} disabled={loading} className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm hover:bg-slate-600 disabled:opacity-50">
          Regenerate Scripts
        </button>
      )}
    </div>
  );
};

// ── ABH Chat Panel ──
const ABHChatPanel = ({ sessionId, currentStep }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    return () => {
      if (sessionId && messages.length > 0) {
        axios.post(`${API}/creative/sessions/${sessionId}/end`).catch(() => {});
      }
    };
  }, [sessionId, messages.length]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/creative/sessions/${sessionId}/chat`, {
        message: msg, step: currentStep === 'framing' ? 0 : currentStep === 'psychology_map' ? 0 : currentStep === 'angles' ? 1 : currentStep === 'scripts' ? 3 : 0,
        step_content: {},
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + (err.response?.data?.detail || err.message) }]);
    } finally {
      setSending(false);
    }
  };

  if (collapsed) {
    return (
      <div onClick={() => setCollapsed(false)} className="bg-slate-800/50 border border-slate-700 rounded-xl p-3 cursor-pointer hover:border-teal-500/30 transition-colors" data-testid="abh-chat-collapsed">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
          <span className="text-sm text-white">ABH Agent</span>
          <span className="text-xs text-slate-500">{messages.length} msgs</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl flex flex-col" style={{height: '400px'}} data-testid="abh-chat-panel">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-700 cursor-pointer" onClick={() => setCollapsed(true)}>
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
          <span className="text-sm font-medium text-white">ABH Creative Agent</span>
          <span className="text-xs text-slate-500">{messages.length} msgs</span>
        </div>
        <span className="text-xs text-slate-500">Sonnet</span>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full"><p className="text-slate-500 text-sm">Ask the ABH creative agent anything</p></div>
        ) : messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-teal-500/10 text-teal-100' : 'bg-slate-700/50 text-slate-200'}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="bg-slate-700/50 rounded-lg px-3 py-2 text-sm text-slate-400 animate-pulse">Thinking...</div></div>}
        <div ref={endRef} />
      </div>

      <div className="p-3 border-t border-slate-700 flex gap-2">
        <input
          type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Ask the ABH agent..." disabled={sending}
          className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none"
          data-testid="abh-chat-input"
        />
        <button onClick={send} disabled={sending || !input.trim()} className="px-3 py-2 bg-teal-500 text-white rounded-lg text-sm disabled:opacity-50" data-testid="abh-chat-send">
          Send
        </button>
      </div>
    </div>
  );
};

// ── Active Session View ──
const ActiveSessionView = ({ session, offer, onBack }) => {
  const [currentStep, setCurrentStep] = useState(session.current_step || 'framing');
  const [selectedAngleId, setSelectedAngleId] = useState(session.selected_angle_id || null);
  const [content, setContent] = useState(null);
  // Custom Session mode runs the Concept → Hook → Script chat engine in place of
  // the guided one-shot ladder. Set at create-time (`_customMode`); on resume,
  // probed from the custom transcript endpoint (no backend change).
  const [customMode, setCustomMode] = useState(!!session._customMode);

  // Resume: load any previously generated content for this session's offer so
  // the phases rehydrate instead of regenerating and discarding prior progress.
  useEffect(() => {
    let active = true;
    axios.get(`${API}/abh/sessions/${session.session_id}/content`)
      .then(r => { if (active) setContent(r.data); })
      .catch(() => { if (active) setContent({ psychology_map: [], angles: [], scripts: [], hooks: [] }); });
    return () => { active = false; };
  }, [session.session_id]);

  // Resume detection: if a custom-session transcript already exists, switch to
  // Custom mode so a reopened custom session lands on the chat engine.
  useEffect(() => {
    if (session._customMode) return;   // already known from create
    let active = true;
    axios.get(`${API}/abh/sessions/${session.session_id}/messages`)
      .then(r => { if (active && r.data && r.data.stage) setCustomMode(true); })
      .catch(() => {});
    return () => { active = false; };
  }, [session.session_id, session._customMode]);

  // Advance + persist server-side so Resume lands on the right step.
  const goToStep = (step, label) => {
    setCurrentStep(step);
    axios.patch(`${API}/abh/sessions/${session.session_id}/step`, { current_step: step, step_label: label }).catch(() => {});
  };
  const handleSelectAngle = (angleId) => {
    setSelectedAngleId(angleId);
    axios.patch(`${API}/abh/sessions/${session.session_id}/step`, { selected_angle_id: angleId }).catch(() => {});
  };

  const steps = [
    { id: 'framing', label: 'Framing' },
    { id: 'psychology_map', label: 'Psychology' },
    { id: 'angles', label: 'Angles' },
    { id: 'scripts', label: 'Scripts' },
    { id: 'complete', label: 'Complete' },
  ];

  // Hold rendering until existing content is loaded, so phases never auto-generate
  // over saved work during the brief fetch window. (Custom mode has its own
  // loader and doesn't depend on the guided content fetch.)
  if (!customMode && !content) {
    return (
      <div className="text-center py-12" data-testid="abh-session-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
        <p className="text-slate-400 text-sm mt-3">Loading session…</p>
      </div>
    );
  }

  const angleScripts = (content?.scripts || []).filter(s => s.angle_id === selectedAngleId);
  const angleHooks = (content?.hooks || []).filter(h => h.angle_id === selectedAngleId);
  const initialScriptData = angleScripts.length > 0
    ? { hooks: angleHooks, scripts: angleScripts, hooks_count: angleHooks.length, scripts_count: angleScripts.length }
    : null;

  return (
    <div className="space-y-4" data-testid="abh-engine-active">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors" data-testid="abh-back-btn">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        </button>
        <div>
          <h2 className="text-lg font-semibold text-white">{offer?.name || 'ABH Session'}</h2>
          <p className="text-slate-500 text-xs">{session.campaign_purpose} / {session.channel}</p>
        </div>
      </div>

      {/* Custom Session mode: the Concept → Hook → Script chat engine replaces
          the guided ladder. One-shot flow below is rendered only when off. */}
      {customMode ? (
        <ABHCustomSession session={session} offer={offer} onBack={onBack} />
      ) : (
      <>
      {/* Step indicator */}
      <div className="flex items-center gap-1">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            <div className={`px-3 py-1 rounded-full text-xs ${
              currentStep === s.id ? 'bg-amber-500 text-white' :
              steps.findIndex(st => st.id === currentStep) > i ? 'bg-teal-500/10 text-teal-400' : 'bg-slate-800 text-slate-500'
            }`} data-testid={`abh-step-${s.id}`}>
              {s.label}
            </div>
            {i < steps.length - 1 && <div className="w-4 h-px bg-slate-700"></div>}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          {currentStep === 'framing' && (
            <FramingPhase session={session} onComplete={() => goToStep('psychology_map', 'Psychology Map')} />
          )}
          {currentStep === 'psychology_map' && (
            <PsychologyMapPhase sessionId={session.session_id} initialEntries={content.psychology_map}
              onComplete={() => goToStep('angles', 'Angle Generation')} />
          )}
          {currentStep === 'angles' && (
            <AnglePhase sessionId={session.session_id} initialAngles={content.angles} initialSelectedAngleId={selectedAngleId}
              onComplete={() => goToStep('scripts', 'Script Generation')} onSelectAngle={handleSelectAngle} />
          )}
          {currentStep === 'scripts' && selectedAngleId && (
            <ScriptPhase sessionId={session.session_id} offerId={offer.offer_id} angleId={selectedAngleId} initialData={initialScriptData}
              onComplete={() => goToStep('complete', 'Complete')} />
          )}
          {currentStep === 'complete' && (
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-6 text-center" data-testid="abh-session-complete">
              <svg className="w-12 h-12 text-teal-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              <h3 className="text-white font-medium">Session Complete</h3>
              <p className="text-slate-400 text-sm mt-1">Brief generated and routed to video editor via Google Drive.</p>
              <button onClick={onBack} className="mt-4 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm" data-testid="new-session-btn">
                Back to ABH Engine
              </button>
            </div>
          )}
        </div>

        {/* Chat panel */}
        <div className="lg:col-span-1">
          {session?.session_id && (
            <ABHChatPanel sessionId={session.session_id} currentStep={currentStep} />
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

// ── Main ABH Creative Engine (self-contained) ──
const ABHCreativeEngine = () => {
  const [searchParams] = useSearchParams();
  const prefill = {
    angle_id: searchParams.get('angle_id'),
    hook_element_id: searchParams.get('hook_element_id'),
    hook_type: searchParams.get('hook_type'),
    cta_element_id: searchParams.get('cta_element_id'),
    channel: searchParams.get('channel'),
    offer_id: searchParams.get('offer_id'),
  };
  const hasPrefill = Object.values(prefill).some(Boolean);

  const [offers, setOffers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [purpose, setPurpose] = useState('Education');
  const [channel, setChannel] = useState(prefill.channel || 'META');
  const [productionType, setProductionType] = useState('AI');
  const [sessionMode, setSessionMode] = useState('guided');   // 'guided' | 'custom' (Concept→Hook→Script chat)
  const [referenceImages, setReferenceImages] = useState([null, null, null]);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [creating, setCreating] = useState(false);

  // Load ABH offers and sessions on mount only. List refresh after
  // create happens imperatively from the createSession handler so
  // this effect has no live deps.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const load = async () => {
      try {
        const [offersRes, sessionsRes] = await Promise.all([
          axios.get(`${API}/abh/offers`),
          axios.get(`${API}/abh/sessions`),
        ]);
        setOffers(offersRes.data.offers || []);
        setSessions(sessionsRes.data.sessions || []);
      } catch (err) {
        console.error('Failed to load ABH data:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Pre-select offer when carrying context from Attribution "Start session" button
  useEffect(() => {
    if (prefill.offer_id && offers.length > 0 && !selectedOffer) {
      const match = offers.find((o) => o.offer_id === prefill.offer_id);
      if (match) setSelectedOffer(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers]);

  const createSession = async () => {
    if (!selectedOffer) return;
    setCreating(true);
    try {
      const payload = {
        offer_id: selectedOffer.offer_id, campaign_purpose: purpose, channel,
        production_type: productionType,
      };
      // Forward Phase 5 prefill hints if present
      if (prefill.angle_id) payload.prefill_angle_id = prefill.angle_id;
      if (prefill.hook_element_id) payload.prefill_hook_element_id = prefill.hook_element_id;
      if (prefill.hook_type) payload.prefill_hook_type = prefill.hook_type;
      if (prefill.cta_element_id) payload.prefill_cta_element_id = prefill.cta_element_id;
      // Reference images — only sent when the operator chose AI Video and at
      // least one slot is populated.
      if (productionType === 'AI') {
        const refs = referenceImages
          .filter((r) => r && r.web_link)
          .map((r) => r.web_link);
        if (refs.length > 0) payload.reference_image_drive_paths = refs;
      }
      const res = await axios.post(`${API}/abh/sessions`, payload);
      // Tag the active session client-side so ActiveSessionView renders the
      // Custom chat engine instead of the guided ladder. The session doc itself
      // is unchanged (no backend change); resume re-detects via /messages.
      setActiveSession({ ...res.data, offer: selectedOffer, _customMode: sessionMode === 'custom' });
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const handleReferenceUpload = async (slotIndex, file) => {
    if (!file) return;
    setUploadingSlot(slotIndex);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/abh/reference-images/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReferenceImages((prev) => {
        const next = [...prev];
        next[slotIndex] = {
          file_id: res.data.file_id,
          web_link: res.data.web_link,
          filename: res.data.filename || file.name,
        };
        return next;
      });
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingSlot(null);
    }
  };

  const clearReferenceSlot = (slotIndex) => {
    setReferenceImages((prev) => {
      const next = [...prev];
      next[slotIndex] = null;
      return next;
    });
  };

  const startNewSessionForOffer = (offerId) => {
    const o = offers.find((x) => x.offer_id === offerId);
    if (o) {
      setSelectedOffer(o);
      // Scroll the New Session form into view so it's obvious where the
      // offer landed.
      setTimeout(() => {
        const node = document.querySelector('[data-testid="abh-new-session-card"]');
        if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    }
  };

  const resumeSession = async (sess) => {
    try {
      const res = await axios.get(`${API}/abh/sessions/${sess.session_id}`);
      const offer = offers.find(o => o.offer_id === sess.offer_id) || { offer_id: sess.offer_id, name: sess.offer_id };
      setActiveSession({ ...res.data, offer });
      setSelectedOffer(offer);
    } catch (err) {
      alert('Failed to load session');
    }
  };

  const handleBack = () => {
    setActiveSession(null);
    setSelectedOffer(null);
    // Refresh sessions list
    axios.get(`${API}/abh/sessions`).then(r => setSessions(r.data.sessions || [])).catch(() => {});
  };

  // Active session view
  if (activeSession) {
    return (
      <ActiveSessionView
        session={activeSession}
        offer={activeSession.offer || selectedOffer}
        onBack={handleBack}
      />
    );
  }

  // Hub view
  return (
    <div className="space-y-6" data-testid="abh-engine-hub">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-500/10 flex items-center justify-center">
          <span className="text-teal-400 font-bold text-lg">A</span>
        </div>
        <div className="abh-section-header">
          <h2 className="text-xl font-semibold text-white" data-testid="abh-engine-title">ABH Creative Engine</h2>
          <p className="text-slate-400 text-sm">AfterBody Health -- GLP-1 Lifecycle Support</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
        </div>
      ) : offers.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 text-center">
          <p className="text-slate-400 text-sm">No ABH offers yet.</p>
          <p className="text-slate-500 text-xs mt-2">Create an offer with the ABH business unit from the Offers page to get started.</p>
        </div>
      ) : (
        <>
          {/* Hook Performance Heatmap — session-start visual */}
          <ABHHookHeatmap />

          {/* Phase 5 — One-click duplicate prefill banner */}
          {hasPrefill && (
            <div
              data-testid="abh-prefill-banner"
              className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center justify-between"
            >
              <div className="text-sm text-emerald-200">
                <strong>Starting from a winner.</strong>{' '}
                {prefill.angle_id && <span>Angle <code>{prefill.angle_id}</code>. </span>}
                {prefill.hook_type && <span>Hook type {prefill.hook_type}. </span>}
                {prefill.channel && <span>Channel {prefill.channel}. </span>}
                The session will be tagged with this context.
              </div>
            </div>
          )}

          {/* New Session */}
          <div
            className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-4"
            data-testid="abh-new-session-card"
          >
            <h3 className="text-white font-medium">New Creative Session</h3>

            {/* Offer picker — searchable dropdown + scrollable shortlist */}
            <div>
              <label className="text-slate-400 text-xs block mb-1.5">Select Offer</label>
              <OfferSearchSelect
                value={selectedOffer?.offer_id || ""}
                onChange={(offerId, offerDoc) => {
                  // Prefer the doc returned by the search result; fall back
                  // to the local `offers` list for business_unit hints.
                  const match = offerDoc || offers.find((x) => x.offer_id === offerId);
                  setSelectedOffer(match || null);
                }}
                placeholder="Type to search — name, code, or id"
                testIdPrefix="abh-offer"
              />
              {offers.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto mt-3">
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                    Or pick from recent
                  </div>
                  {offers.slice(0, 20).map(o => (
                    <div
                      key={o.offer_id}
                      onClick={() => setSelectedOffer(o)}
                      className={`p-2.5 rounded-lg cursor-pointer border ${selectedOffer?.offer_id === o.offer_id ? 'bg-teal-500/10 border-teal-500' : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'}`}
                      data-testid={`offer-select-${o.offer_id}`}>
                      <div className="text-white text-sm">{o.name}</div>
                      <div className="text-slate-500 text-xs">
                        Payout ${o.payout} · Tier {o.tier || '?'} · {o.discovery_source}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedOffer && (
              <>
                <div>
                  <label className="text-slate-400 text-xs block mb-1.5">Campaign Purpose</label>
                  <div className="flex gap-2">
                    {['Education', 'Conversion', 'Engagement'].map(p => (
                      <button key={p} onClick={() => setPurpose(p)} data-testid={`purpose-${p.toLowerCase()}`}
                        className={`px-4 py-2 rounded-lg text-sm ${purpose === p ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1.5">Channel</label>
                  <div className="flex gap-2">
                    {['META', 'YOUTUBE', 'TIKTOK'].map(c => (
                      <button key={c} onClick={() => setChannel(c)} data-testid={`channel-${c.toLowerCase()}`}
                        className={`px-4 py-2 rounded-lg text-sm ${channel === c ? 'bg-teal-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1.5">Production Type</label>
                  <div className="flex gap-2 flex-wrap">
                    {/* `AI` replaced `VEO` on 2026-09-02 — Veo is retired and was a
                        tool name being used as a format name. The stored value is
                        `AI`; the label is what the operator reads.

                        There is no TRELLO/COMFY split here, and that is deliberate
                        rather than an omission: an ABH brief goes to Drive and
                        Telegram, never to Trello (`routers/ops.py`, abh_route_to_
                        production). Offering an "AI VIDEO TRELLO" option on this
                        screen would be a label that lies, which is the exact defect
                        this lane exists to remove. */}
                    {[
                      { value: 'AI', label: 'AI Video' },
                      { value: 'VE', label: 'VE' },
                      { value: 'UGC', label: 'UGC' },
                      { value: 'IMAGE', label: 'IMAGE' },
                    ].map(pt => (
                      <button
                        key={pt.value}
                        onClick={() => setProductionType(pt.value)}
                        data-testid={`production-type-${pt.value.toLowerCase()}`}
                        className={`px-4 py-2 rounded-lg text-sm ${productionType === pt.value ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}
                      >
                        {pt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1.5">Session Mode</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'guided', label: 'Guided' },
                      { id: 'custom', label: 'Custom Chat' },
                    ].map(m => (
                      <button
                        key={m.id}
                        onClick={() => setSessionMode(m.id)}
                        data-testid={`session-mode-${m.id}`}
                        className={`px-4 py-2 rounded-lg text-sm ${sessionMode === m.id ? 'bg-teal-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-slate-500 text-xs mt-1">
                    {sessionMode === 'custom'
                      ? 'Concept → Hook → Script chat — refine and lock each stage yourself.'
                      : 'The standard guided flow (framing → psychology map → angles → scripts).'}
                  </p>
                </div>
                {productionType === 'AI' && (
                  <div data-testid="reference-images-picker">
                    <label className="text-slate-400 text-xs block mb-1.5">
                      Reference Images <span className="text-slate-500">(optional · up to 3 · Ingredients-to-Video)</span>
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {[0, 1, 2].map((i) => {
                        const slot = referenceImages[i];
                        return (
                          <div
                            key={i}
                            className="bg-slate-900/50 border border-dashed border-slate-700 rounded-lg p-2 text-center"
                            data-testid={`reference-slot-${i + 1}`}
                          >
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                              Reference Image {i + 1}
                            </div>
                            {slot ? (
                              <>
                                <a
                                  href={slot.web_link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block text-[11px] text-emerald-400 truncate hover:underline"
                                  title={slot.filename}
                                >
                                  ✓ {slot.filename || 'uploaded'}
                                </a>
                                <button
                                  onClick={() => clearReferenceSlot(i)}
                                  className="mt-1 text-[10px] text-rose-400 hover:text-rose-300"
                                  data-testid={`reference-clear-${i + 1}`}
                                >
                                  clear
                                </button>
                              </>
                            ) : uploadingSlot === i ? (
                              <div className="text-[11px] text-amber-300">Uploading…</div>
                            ) : (
                              <label
                                className="cursor-pointer block text-[11px] text-slate-400 hover:text-white py-2"
                                data-testid={`reference-upload-${i + 1}`}
                              >
                                + upload PNG/JPEG
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleReferenceUpload(i, file);
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1.5">
                      Reference images go to the video editor with the brief, so all clips
                      keep ABH product visuals consistent. Stored in <em>Product Assets &amp; Branding</em>.
                    </p>
                  </div>
                )}
                <button onClick={createSession} disabled={creating} data-testid="create-abh-session-btn"
                  className="w-full px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                  {creating ? 'Starting...' : 'Start ABH Session'}
                </button>
              </>
            )}
          </div>

          {/* Previous Sessions */}
          {sessions.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-slate-400 text-sm font-medium">Previous Sessions</h3>
              <p className="text-slate-500 text-xs">
                Click an offer name to start a <strong>new</strong> session for that offer.
                Use the <em>Resume</em> link to reopen the existing session.
              </p>
              {sessions.slice(0, 10).map(s => {
                const offerName = offers.find(o => o.offer_id === s.offer_id)?.name || s.offer_id;
                const dateStr = s.created_at
                  ? new Date(s.created_at).toLocaleString(undefined, {
                      year: 'numeric', month: 'numeric', day: 'numeric',
                      hour: 'numeric', minute: '2-digit', hour12: true
                    })
                  : '';
                return (
                  <div key={s.session_id}
                    className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 hover:border-slate-600 transition-colors"
                    data-testid={`abh-prev-session-${s.session_id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <button
                        type="button"
                        onClick={() => startNewSessionForOffer(s.offer_id)}
                        className="flex-1 text-left cursor-pointer hover:text-teal-300"
                        data-testid={`abh-start-new-from-${s.offer_id}`}
                        title="Start a NEW session with this offer pre-selected"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium">Session {s.session_number ?? '—'}</span>
                          <span className="text-slate-500 text-xs">·</span>
                          <span className="text-slate-300 text-sm">{offerName}</span>
                        </div>
                        <span className="text-slate-500 text-xs">{s.campaign_purpose} / {s.channel}</span>
                        {s.status === 'active' && s.step_label && (
                          <p className="text-slate-400 text-xs mt-0.5">paused at {s.step_label}</p>
                        )}
                        <p className="text-slate-500 text-xs mt-0.5">{dateStr}</p>
                      </button>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          s.status === 'active' ? 'bg-teal-500/10 text-teal-400'
                          : s.status === 'completed' ? 'bg-blue-500/15 text-blue-400'
                          : s.status === 'abandoned' ? 'bg-slate-600/30 text-slate-400'
                          : 'bg-slate-700 text-slate-400'
                        }`}>{s.status}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); resumeSession(s); }}
                          className="text-xs text-slate-400 hover:text-white underline-offset-2 hover:underline"
                          data-testid={`resume-session-${s.session_id}`}
                        >
                          Resume
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ABHCreativeEngine;
